import axios from "axios";
import { sessionView } from "../state/views";
import { logout } from "../utils";
import { decodeJwtPayload } from "./authHandoff";
import { clearSessionId } from "./mixpanel";

const normalizeOptionalString = (value) => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const resolveBrowserOrigin = () =>
  typeof window !== "undefined" ? window.location.origin : null;

const resolveRequestUrl = (config = {}) => {
  const rawUrl = normalizeOptionalString(config?.url);
  if (!rawUrl) return null;

  const rawBaseUrl =
    normalizeOptionalString(config?.baseURL) ||
    normalizeOptionalString(process.env.API_URL) ||
    resolveBrowserOrigin();

  try {
    return new URL(rawUrl, rawBaseUrl || undefined).toString();
  } catch (_error) {
    return rawUrl;
  }
};

const resolveCorrelationIdFromHeaders = (headers = {}) => {
  if (!headers || typeof headers !== "object") return null;
  return (
    normalizeOptionalString(headers["x-correlation-id"]) ||
    normalizeOptionalString(headers["X-Correlation-Id"]) ||
    null
  );
};

export const describeHttpClientRequestFailure = (error = {}, details = {}) => {
  const requestUrl =
    normalizeOptionalString(details.requestUrl) ||
    resolveRequestUrl(error?.config) ||
    null;
  const status = Number(error?.response?.status || error?.status || 0) || null;
  const code = normalizeOptionalString(error?.code);
  const message = normalizeOptionalString(error?.message);
  const browserOrigin = resolveBrowserOrigin();
  const isNetworkLikeError =
    !status && (code === "ERR_NETWORK" || message === "Network Error");
  let isLikelyCorsError = false;

  if (isNetworkLikeError && browserOrigin && requestUrl) {
    try {
      isLikelyCorsError =
        new URL(requestUrl, browserOrigin).origin !== browserOrigin &&
        (typeof navigator === "undefined" || navigator.onLine !== false);
    } catch (_error) {}
  }

  let errorType = null;

  if (code === "ECONNABORTED") {
    errorType = "timeout";
  } else if (isNetworkLikeError) {
    errorType = isLikelyCorsError ? "cors" : "network";
  }

  return {
    status,
    code,
    errorCode: code,
    message,
    requestUrl,
    errorType,
    endpointType: normalizeOptionalString(details.endpointType),
    correlationId:
      normalizeOptionalString(details.correlationId) ||
      normalizeOptionalString(error?.correlationId) ||
      normalizeOptionalString(error?.response?.data?.correlationId) ||
      resolveCorrelationIdFromHeaders(error?.response?.headers) ||
      null,
  };
};

export const tagHttpClientRequestFailure = (error = {}, details = {}) => {
  const metadata = describeHttpClientRequestFailure(error, details);

  if (metadata.errorType) {
    error.disclosureErrorType = metadata.errorType;
  }
  if (metadata.errorCode) {
    error.disclosureErrorCode = metadata.errorCode;
  }
  if (metadata.endpointType) {
    error.disclosureEndpointType = metadata.endpointType;
  }
  if (metadata.requestUrl) {
    error.disclosureRequestUrl = metadata.requestUrl;
  }
  if (metadata.correlationId && !error.correlationId) {
    error.correlationId = metadata.correlationId;
  }

  return error;
};

export const shouldLogoutForForbiddenResponse = (error = {}, authenticated) =>
  error?.response?.status === 403 &&
  authenticated &&
  !error?.config?.skipSessionExpirationLogout;

const httpClient = axios.create({
  baseURL: process.env.API_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

httpClient.interceptors.request.use((config) => {
  // add auth headers to each request
  const newHeaders = {
    "x-api-key": process.env.API_KEY,
  };

  const session = sessionView.read().state || {};
  const authToken = config.useUnifiedAuthToken
    ? session.unifiedAuthToken || session.authToken
    : session.authToken;
  const explicitAuthorization =
    config.headers?.Authorization ||
    config.headers?.authorization ||
    config.headers?.get?.("Authorization");

  if (authToken && !explicitAuthorization) {
    newHeaders.Authorization = `Bearer ${authToken}`;
  }

  config.headers = {
    ...config.headers,
    ...newHeaders,
  };

  return config;
});

httpClient.interceptors.response.use(
  // handle token refresh in response
  async (response) => {
    // Check both top-level and nested response structures for authToken
    const authToken =
      response.data?.authToken || response.data?.data?.authToken;

    if (authToken && !response.config?.skipAuthTokenSessionWrite) {
      const payload = decodeJwtPayload(authToken);
      const expirationSeconds = Number(payload?.expires ?? payload?.exp);
      const expires = expirationSeconds * 1000;

      if (
        expirationSeconds > 0 &&
        Number.isFinite(expires) &&
        expires > Date.now()
      ) {
        const idToken =
          response.data?.idToken || response.data?.data?.idToken || "";
        const accessToken =
          response.data?.accessToken || response.data?.data?.accessToken || "";
        const currentSession = sessionView.read().state || {};
        const currentExpires = Number(currentSession.expires);
        const sessionExpires =
          currentSession.unifiedAuthToken &&
          Number.isFinite(currentExpires) &&
          currentExpires > Date.now()
            ? Math.min(expires, currentExpires)
            : expires;

        try {
          await sessionView.write({
            authToken,
            unifiedAuthToken: currentSession.unifiedAuthToken || "",
            idToken: idToken || currentSession.idToken || "",
            accessToken: accessToken || currentSession.accessToken || "",
            expires: sessionExpires,
            authenticated: true,
          });
        } catch (_error) {}
      }
    }

    if (authToken && response.config?.skipAuthTokenSessionWrite) {
      const payload = response.data || {};
      const nestedPayload =
        payload.data &&
        !Array.isArray(payload.data) &&
        typeof payload.data === "object"
          ? payload.data
          : {};

      return {
        ...payload,
        ...nestedPayload,
      };
    }

    return response?.data?.data || response?.data;
  },
  // handle session expiration in response
  (err) => {
    if (
      shouldLogoutForForbiddenResponse(
        err,
        sessionView.read().state?.authenticated,
      )
    ) {
      // session expired, logout
      alert("Session expired. Returning to login page.");
      clearSessionId();
      logout();
    }

    return Promise.reject(err);
  },
);

export default httpClient;
